import type { HTMLAttributes, PropsWithChildren } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import type {
  AutoSizeValue,
  FontValue
} from '@elfsight/configurator-contracts';
import { parseWidth } from '@elfsight/widget-utils';

const STATIC_CLASS = 'es-embed-root';

const Root = styled.div<{
  _font?: FontValue;
  _textColor?: string;
  _fullWidth?: boolean;
}>`
  position: relative;
  width: ${({ _fullWidth }) => (_fullWidth ? '100%' : 'fit-content')};
  color: ${({ _textColor = 'inherit' }) => _textColor};
  margin: 0 auto;

  -webkit-font-smoothing: antialiased;

  &,
  * {
    box-sizing: border-box !important;
    outline: none !important;
  }

  ${({ _font }) => _font?.value && `font-family: "${_font.value}";`}
`;

export type EmbedRootProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    width?: string | number | AutoSizeValue;
    fullWidth?: boolean;
    textColor?: string;
    customCSSClassName?: string;
    font?: FontValue;
  }
>;

export const EmbedRoot = forwardRef<HTMLDivElement, EmbedRootProps>(function (
  {
    children,
    fullWidth = true,
    width = 'auto',
    textColor,
    className,
    customCSSClassName,
    font,
    style,
    ...forwardingProps
  },
  ref
) {
  const fullClassName = [STATIC_CLASS, customCSSClassName, className]
    .filter(Boolean)
    .join(' ');

  return (
    <Root
      {...forwardingProps}
      className={fullClassName}
      style={{ ...style, maxWidth: parseWidth(width) }}
      _font={font}
      _textColor={textColor}
      _fullWidth={fullWidth}
      ref={ref}
    >
      {children}
    </Root>
  );
});
